import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate } from "react-router-dom";
import { Heart, MessageSquare, BarChart3, Users, Building2, ShieldCheck, Plus, Trash2, Star, BadgeCheck, FileText, Pencil, Eye, EyeOff, CheckCircle2, Lock, ArrowRight, Sparkles, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatINR, fileUrl } from "../lib/api";
import { toast } from "sonner";
import AdminDashboard from "./AdminDashboard";
import { KYCSection, WalletSection } from "../components/PartnerKYCWallet";
import { SubscriptionCard } from "../components/SubscriptionCard";
import ReferEarnCard from "../components/ReferEarnCard";
import { isAdmin, isServiceRole, isListingRole } from "../lib/roles";

// Strict role isolation:
//   admin        → AdminDashboard
//   gpm_partner  → redirected to /partner/dashboard (Partner-specific UI only)
//   service pros → redirected to /my-services (Portfolio + Enquiries; no property-listing options)
//   broker/seller/builder → PartnerDash (property listings, leads, KYC)
//   everyone else (buyer/tenant) → UserDash (saved + inquiries)
export default function Dashboard() {
    const { user } = useAuth();
    if (!user) return null;
    if (isAdmin(user.role)) return <AdminDashboard />;
    if (user.role === "gpm_partner") return <Navigate to="/partner/dashboard" replace />;
    if (isServiceRole(user.role)) return <Navigate to="/my-services" replace />;
    if (isListingRole(user.role)) return <PartnerDash user={user} />;
    return <UserDash user={user} />;
}

/* -------------------- USER -------------------- */
function UserDash({ user }) {
    const [saved, setSaved] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    useEffect(() => {
        api.get("/saved").then((r) => setSaved(r.data || []));
        api.get("/inquiries/mine").then((r) => setInquiries(r.data || []));
    }, []);
    return (
        <DashShell user={user} title="My Dashboard">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard icon={Heart} label="Saved Properties" value={saved.length} />
                <StatCard icon={MessageSquare} label="My Inquiries" value={inquiries.length} />
                <StatCard icon={BadgeCheck} label="Account Type" value="Buyer" />
            </div>

            <ReferEarnCard theme="dark" />
            <Section title="Saved properties">
                {saved.length === 0 ? (
                    <Empty msg="You haven't saved any properties yet." cta="Browse properties" to="/properties" />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {saved.map((p) => (
                            <Link key={p.id} to={`/properties/${p.id}`} className="rounded-xl bg-navy-900 border border-white/5 hover:border-gold-500/40 p-4">
                                <div className="aspect-[16/10] rounded-md overflow-hidden bg-navy-800 mb-3">
                                    {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div className="font-heading font-bold text-white text-sm line-clamp-1">{p.title}</div>
                                <div className="text-gold-500 font-bold mt-1">{formatINR(p.price)}</div>
                            </Link>
                        ))}
                    </div>
                )}
            </Section>

            <SubscriptionCard />
            <Section title="My inquiries">
                {inquiries.length === 0 ? (
                    <Empty msg="No inquiries yet." cta="Find properties" to="/properties" />
                ) : (
                    <div className="space-y-2">
                        {inquiries.map((i) => (
                            <div key={i.id} className="rounded-lg bg-navy-900 border border-white/5 p-4 flex items-center justify-between">
                                <div>
                                    <div className="font-semibold text-white text-sm">{i.property_title}</div>
                                    <div className="text-xs text-white/55 mt-0.5">Sent on {new Date(i.created_at).toLocaleDateString()}</div>
                                </div>
                                <span className="text-[10px] uppercase tracking-widest text-gold-500 px-2 py-1 rounded-full bg-gold-500/10 border border-gold-500/30">{i.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            <WalletSection user={user} />
        </DashShell>
    );
}

/* -------------------- PARTNER -------------------- */
function PartnerDash({ user }) {
    const [listings, setListings] = useState([]);
    const [leads, setLeads] = useState([]);
    const reload = () => {
        api.get("/partners/me/listings").then((r) => setListings(r.data || []));
        api.get("/inquiries/leads").then((r) => setLeads(r.data || []));
    };
    useEffect(() => { reload(); }, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this listing? This cannot be undone.")) return;
        await api.delete(`/properties/${id}`);
        toast.success("Deleted");
        reload();
    };

    const onSetStatus = async (id, newStatus, label) => {
        const messages = {
            hidden: "Hide this listing from public marketplace?",
            active: "Show this listing publicly again?",
            sold: "Mark this listing as SOLD? It will be visually marked as sold and removed from active search.",
        };
        if (!window.confirm(messages[newStatus])) return;
        try {
            await api.put(`/properties/${id}/status`, null, { params: { status: newStatus } });
            toast.success(label);
            reload();
        } catch (e) {
            toast.error("Failed to update status");
        }
    };

    const dashTitle = {
        broker: "Broker Dashboard",
        seller: "Seller Dashboard",
        builder: "Builder Dashboard",
    }[user.role] || "Partner Dashboard";

    return (
        <DashShell user={user} title={dashTitle} cta={
            <Link to="/add-property" data-testid="add-property-cta" className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-gold-100 shadow-md shadow-gold-500/20">
                <Plus className="w-4 h-4" /> Add Property
            </Link>
        }>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Building2} label="Active Listings" value={listings.length} />
                <StatCard icon={MessageSquare} label="Total Leads" value={leads.length} />
                <StatCard icon={Star} label="Rating" value={(user.rating || 0).toFixed(1)} />
                <StatCard icon={ShieldCheck} label="Verified" value={user.verified ? "Yes" : "Pending"} />
            </div>

            <ReferEarnCard theme="dark" />
            <Section title="My listings">
                {listings.length === 0 ? (
                    <Empty msg="No listings yet." cta="Add your first listing" to="/add-property" />
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-white/5">
                        <table className="w-full text-sm">
                            <thead className="bg-navy-950 text-xs uppercase tracking-wider text-white/50">
                                <tr>
                                    <th className="text-left px-4 py-3">Property</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">City</th>
                                    <th className="text-right px-4 py-3">Price</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {listings.map((p) => {
                                    const status = p.status || "active";
                                    const isSold = status === "sold";
                                    const isHidden = status === "hidden";
                                    return (
                                    <tr key={p.id} className={`bg-navy-900 hover:bg-navy-800/60 ${isSold ? "opacity-60" : ""}`} data-testid={`listing-row-${p.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Link to={`/properties/${p.id}`} className="font-semibold text-white hover:text-gold-500">{p.title}</Link>
                                                {isSold && <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40">Sold</span>}
                                                {isHidden && <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/60 border border-white/20">Hidden</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-white/70 capitalize">{p.property_type} · {p.listing_type}</td>
                                        <td className="px-4 py-3 text-white/70">{p.city}</td>
                                        <td className="px-4 py-3 text-right text-gold-500 font-bold">{formatINR(p.price)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                                                {!isSold && (
                                                    <button
                                                        data-testid={`sold-listing-${p.id}`}
                                                        onClick={() => onSetStatus(p.id, "sold", "Marked as sold")}
                                                        className="text-white/50 hover:text-emerald-400 p-1.5 rounded-md hover:bg-emerald-500/10"
                                                        title="Mark as sold"
                                                    >
                                                        <CheckCircle2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {isHidden ? (
                                                    <button
                                                        data-testid={`show-listing-${p.id}`}
                                                        onClick={() => onSetStatus(p.id, "active", "Listing is now visible")}
                                                        className="text-emerald-400 hover:text-emerald-300 p-1.5 rounded-md hover:bg-emerald-500/10"
                                                        title="Show publicly"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        data-testid={`hide-listing-${p.id}`}
                                                        onClick={() => onSetStatus(p.id, "hidden", "Listing hidden")}
                                                        className="text-white/50 hover:text-amber-400 p-1.5 rounded-md hover:bg-amber-500/10"
                                                        title="Hide from public"
                                                    >
                                                        <EyeOff className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <Link
                                                    to={`/edit-property/${p.id}`}
                                                    data-testid={`edit-listing-${p.id}`}
                                                    className="text-white/50 hover:text-gold-500 p-1.5 rounded-md hover:bg-gold-500/10"
                                                    title="Edit"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Link>
                                                <button data-testid={`delete-listing-${p.id}`} onClick={() => onDelete(p.id)} className="text-white/50 hover:text-red-400 p-1.5 rounded-md hover:bg-red-500/10" title="Delete">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            <Section title="Recent leads">
                {leads.length === 0 ? (
                    <Empty msg="No leads yet — listings drive leads, add more!" />
                ) : (
                    <div className="space-y-2">
                        {leads.map((l) => (
                            <div key={l.id} className="rounded-lg bg-navy-900 border border-white/5 p-4">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                    <div>
                                        <div className="font-semibold text-white">{l.name} · {l.phone}</div>
                                        <div className="text-xs text-white/55">{l.email} · for "{l.property_title}"</div>
                                    </div>
                                    <div className="text-xs text-white/50">{new Date(l.created_at).toLocaleString()}</div>
                                </div>
                                {l.message && <p className="text-sm text-white/70 mt-2 italic">"{l.message}"</p>}
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            <KYCSection />
            <SubscriptionCard />
            <WalletSection user={user} />
        </DashShell>
    );
}

/* -------------------- Helpers -------------------- */
function DashShell({ user, title, cta, children }) {
    return (
        <div className="min-h-screen bg-navy-950">
            <div className="bg-navy-900 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="label-overline mb-2">{user.role === "admin" ? "Admin" : (user.role === "user" || user.role === "buyer") ? "Buyer" : "Partner"}</div>
                        <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white">{title}</h1>
                        <p className="text-white/55 text-sm mt-1">Welcome back, <span className="text-gold-500 font-medium">{user.name}</span></p>
                    </div>
                    {cta}
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 space-y-10" data-testid="dashboard-content">
                <OverLimitGate />
                {children}
            </div>
        </div>
    );
}

/** Fetches property quota and shows a prominent upgrade banner + one-time modal
 *  if the user has more listings than the FREE plan allows. */
function OverLimitGate() {
    const [quota, setQuota] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [dismissedModal, setDismissedModal] = useState(() => {
        try { return sessionStorage.getItem("gpm_overlimit_modal_dismissed") === "1"; } catch { return false; }
    });

    useEffect(() => {
        api.get("/me/property-quota")
            .then((r) => {
                setQuota(r.data);
                if (r.data?.needs_upgrade && !dismissedModal) setModalOpen(true);
            })
            .catch(() => { /* silent */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeModal = () => {
        setModalOpen(false);
        setDismissedModal(true);
        try { sessionStorage.setItem("gpm_overlimit_modal_dismissed", "1"); } catch { /* ignore */ }
    };

    if (!quota?.needs_upgrade) return null;

    return (
        <>
            <div className="rounded-xl border border-gold-500/40 bg-gradient-to-r from-gold-500/15 to-amber-500/10 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="dash-overlimit-banner">
                <Lock className="w-5 h-5 text-gold-400 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">
                        FREE plan limit exceeded — {quota.used} properties on a 1-property plan
                    </div>
                    <div className="text-xs text-white/70 mt-0.5">
                        Upgrade to keep every listing active and unlock unlimited properties + featured placement.
                    </div>
                </div>
                <Link
                    to="/pricing"
                    data-testid="dash-overlimit-upgrade-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-gold-500 text-navy-900 text-xs font-extrabold uppercase tracking-wider hover:bg-gold-400"
                >
                    Upgrade Plan <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </div>

            {modalOpen && createPortal(
                <OverLimitModal quota={quota} onClose={closeModal} />,
                document.body
            )}
        </>
    );
}

function OverLimitModal({ quota, onClose }) {
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.scrollTo({ top: 0, behavior: "instant" });
        return () => { document.body.style.overflow = prev; };
    }, []);
    return (
        <div
            className="fixed inset-0 z-[10000] bg-navy-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={onClose}
            data-testid="dash-overlimit-modal"
        >
            <div
                className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="shrink-0 bg-gradient-to-br from-navy-500 to-navy-700 text-white px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-gold-500/20 border border-gold-500/40 flex items-center justify-center shrink-0">
                            <Lock className="w-5 h-5 text-gold-400" />
                        </div>
                        <div>
                            <div className="font-heading text-lg font-bold">Action required</div>
                            <div className="text-[11px] uppercase tracking-widest text-gold-300/90 mt-0.5">Please upgrade your plan</div>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="text-white/60 hover:text-white p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="overflow-y-auto px-6 py-5">
                    <p className="text-sm text-navy-500 leading-relaxed">
                        Your FREE plan includes only <strong>1 Property Listing</strong>. You currently
                        have <strong>{quota.used} properties</strong>. Please upgrade your subscription
                        plan to keep every listing active and add more properties.
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-navy-500/80">
                        <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" /> Unlimited property listings</li>
                        <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" /> Featured placement + verified badge</li>
                        <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" /> Priority support and faster lead responses</li>
                    </ul>
                </div>
                <div className="shrink-0 border-t border-navy-500/10 px-6 py-3 flex gap-2 bg-white">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-2.5 rounded-md border border-navy-500/20 text-navy-500 text-sm font-semibold hover:bg-navy-500/5"
                        data-testid="dash-overlimit-modal-later"
                    >
                        Remind me later
                    </button>
                    <Link
                        to="/pricing"
                        onClick={onClose}
                        data-testid="dash-overlimit-modal-cta"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 text-sm font-extrabold hover:bg-gold-400"
                    >
                        View Plans <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="rounded-xl bg-navy-900 border border-white/5 p-5">
            <div className="flex items-center justify-between">
                <Icon className="w-5 h-5 text-gold-500" />
            </div>
            <div className="font-heading text-3xl font-bold text-white mt-3">{value}</div>
            <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{label}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <h2 className="font-heading text-xl font-bold text-white mb-4">{title}</h2>
            {children}
        </div>
    );
}

function Empty({ msg, cta, to }) {
    return (
        <div className="rounded-xl bg-navy-900 border border-white/5 p-10 text-center">
            <p className="text-white/60 text-sm">{msg}</p>
            {cta && to && <Link to={to} className="inline-block mt-4 px-5 py-2 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100">{cta}</Link>}
        </div>
    );
}
